import React, { useState, useEffect } from "react";
import { Users, UserPlus, XCircle } from "lucide-react";
import { apiClient } from "../../../utils/apiClient/index.ts";
import { useAlert } from "../../../hooks/useAlert";

import type { CaseAssignment, OrganizationUser } from "../../../types";
import Button from "../../../components/common/ui/button/Button";
import AssignUserModal from "./AssignUserModal";

import { Table, TableColumn } from "../../../components/common/tables/Table";
import { TableContainer } from "../../../components/common/tables/TableContainer";
import {
  IconWithTextCell,
  DateCell,
  TextCell,
} from "../../../components/common/tables/cells";
import { toTitleCase } from "@secure-caseworker/utils";

interface AssignmentsTabProps {
  caseId: string;
  organizationId?: string;
  onAssignmentChange?: () => void;
}

interface AssignmentWithRole extends CaseAssignment {
  role?: string;
}

const AssignmentsTab: React.FC<AssignmentsTabProps> = ({
  caseId,
  organizationId: propOrganizationId,
  onAssignmentChange,
}) => {
  const { setAlert } = useAlert();
  const [assignments, setAssignments] = useState<AssignmentWithRole[]>([]);
  const [organizationId, setOrganizationId] = useState<string | null>(
    propOrganizationId || null
  );
  const [loading, setLoading] = useState(true);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);

  useEffect(() => {
    loadAssignments();
  }, [caseId]);

  const loadAssignments = async () => {
    setLoading(true);
    try {
      // Load case to get organization ID if not provided
      let orgId = organizationId;
      if (!orgId) {
        const caseData = await apiClient.getCase(caseId);
        orgId = caseData.organizationId;
        setOrganizationId(orgId);
      }

      // Load current assignments
      const assignmentsData = await apiClient.getCaseAssignments(caseId);

      // Load organization members to get roles
      const orgMembers = await apiClient.listOrganizationMembers(orgId);

      // Create a map of userId to role
      const userRoleMap = new Map<string, string>();
      orgMembers.forEach((member: OrganizationUser) => {
        userRoleMap.set(member.userId, member.role);
      });

      // Merge assignments with roles
      const assignmentsWithRoles: AssignmentWithRole[] = assignmentsData.map(
        (assignment) => ({
          ...assignment,
          role: userRoleMap.get(assignment.userId) || "Unknown",
        })
      );

      setAssignments(assignmentsWithRoles);
    } catch (error) {
      setAlert({
        variant: "error",
        title: "Error",
        message: "Failed to load assignments",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveAssignment = async (userId: string) => {
    if (!confirm("Are you sure you want to remove this user from the case?"))
      return;

    try {
      await apiClient.removeUserFromCase(caseId, userId);
      setAlert({
        variant: "success",
        title: "Success",
        message: "User removed from case",
      });
      loadAssignments();
      onAssignmentChange?.();
    } catch (error) {
      setAlert({
        variant: "error",
        title: "Error",
        message: "Failed to remove user",
      });
    }
  };

  const handleAssignSuccess = () => {
    loadAssignments();
    onAssignmentChange?.();
  };

  const columns: TableColumn<AssignmentWithRole>[] = [
    {
      header: "Name",
      accessor: "user",
      render: (_value, row) => (
        <IconWithTextCell
          icon={Users}
          iconColor="brand"
          primaryText={row.user?.fullName || row.user?.email || "Unknown User"}
          secondaryText={row.user?.email}
        />
      ),
    },
    {
      header: "Role",
      accessor: "role",
      render: (value) => (
        <TextCell>{toTitleCase(value ?? "") || "Unknown"}</TextCell>
      ),
    },
    {
      header: "Assigned Date",
      accessor: "assignedAt",
      render: (value) => <DateCell date={value} />,
    },
    {
      header: "Actions",
      accessor: "id",
      render: (_value, row) => (
        <Button
          variant="danger"
          size="xs"
          onClick={() => handleRemoveAssignment(row.userId)}
        >
          <XCircle className="h-4 w-4" />
        </Button>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <TableContainer
        loading={loading}
        loadingText="Loading assignments..."
        isEmpty={assignments.length === 0}
        emptyIcon={<Users size={48} />}
        emptyTitle="No users assigned to this case"
        emptyDescription='Click "Assign Users" to add team members'
        title="Case Access"
        titleIcon={<Users size={20} />}
        count={assignments.length}
        headerAction={
          <Button
            onClick={() => setIsAssignModalOpen(true)}
            startIcon={<UserPlus size={18} />}
          >
            Assign Users
          </Button>
        }
      >
        <Table columns={columns} data={assignments} />
      </TableContainer>

      {/* Assign User Modal */}
      {organizationId && (
        <AssignUserModal
          isOpen={isAssignModalOpen}
          onClose={() => setIsAssignModalOpen(false)}
          caseId={caseId}
          organizationId={organizationId}
          onSuccess={handleAssignSuccess}
        />
      )}
    </div>
  );
};

export default AssignmentsTab;
